import { useCallback, useEffect, useState } from 'react'

import { Box } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { CustomSnackbarProvider } from 'app/lib/components/CustomSnackbar'
import { useIsSuperAdmin } from 'app/lib/hoc/withProtectedComponent'
import { OrganizationEngagementResourceType } from 'app/models/Organization'
import { OrganizationRoute } from 'app/models/scribe.models'
import {
  useCreateOrganizationResourceMutation,
  useDeleteOrganizationResourceMutation,
  useGetOrganizationResourcesQuery,
  useUpdateOrganizationResourceMutation,
} from 'app/redux/scribeApi'

import { AdminFreeResourcesCard } from './admin-free-resources-card/AdminFreeResourcesCard'
import { SuperAdminEngagementResourcesEditForm } from './super-admin-engagement-resources-edit-form/SuperAdminEngagementResourcesEditForm'

type ResourceBody = { resourceType: OrganizationEngagementResourceType; resourceAsset: string }

type GetSaveActionParams = {
  linkValue: string
  existingResourceId: number | undefined
  organizationId: string
  body: ResourceBody
  deleteResource: ReturnType<typeof useDeleteOrganizationResourceMutation>[0]
  updateResource: ReturnType<typeof useUpdateOrganizationResourceMutation>[0]
  createResource: ReturnType<typeof useCreateOrganizationResourceMutation>[0]
}

const getSaveAction = ({
  linkValue,
  existingResourceId,
  organizationId,
  body,
  deleteResource,
  updateResource,
  createResource,
}: GetSaveActionParams) => {
  if (!linkValue && existingResourceId) {
    return deleteResource({ organizationId, organizationResourceId: existingResourceId })
  }
  if (existingResourceId) {
    return updateResource({ organizationId, organizationResourceId: existingResourceId, body })
  }
  if (linkValue) {
    return createResource({ organizationId, body })
  }
  return null
}

export const OrganizationEngagementResourcesPage = () => (
  <Box sx={{ position: 'relative', height: '100%', transform: 'translateZ(0)' }}>
    <CustomSnackbarProvider>
      <EngagementResourcesContent />
    </CustomSnackbarProvider>
  </Box>
)

const EngagementResourcesContent = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'engagementResourcesPage' })
  const { organizationId } = useParams() as OrganizationRoute
  const { data: resources, isError, isFetching } = useGetOrganizationResourcesQuery(organizationId)
  const [deleteResource, { isLoading: isDeleting }] = useDeleteOrganizationResourceMutation()
  const [createResource, { isLoading: isCreating }] = useCreateOrganizationResourceMutation()
  const [updateResource, { isLoading: isUpdating }] = useUpdateOrganizationResourceMutation()
  const isSaving = isDeleting || isCreating || isUpdating
  const { enqueueSnackbar } = useSnackbar()
  const isSuperAdmin = useIsSuperAdmin()

  const resourceAsset = resources?.[0]?.resourceAsset
  const [isEditing, setIsEditing] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const hasNoChanges = linkValue === (resourceAsset ?? '')
  const isSaveDisabled = isSaving || hasNoChanges

  useEffect(() => {
    if (isError) enqueueSnackbar(t('freeResources.error'), { variant: 'error' })
  }, [isError, enqueueSnackbar, t])

  const handleEditClick = useCallback(() => {
    setLinkValue(resourceAsset ?? '')
    setIsEditing(true)
  }, [resourceAsset])

  const handleCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSave = useCallback(async () => {
    const existingResourceId = resources?.[0]?.id
    const body = {
      resourceType: OrganizationEngagementResourceType.HIGHSPOT_LINK,
      resourceAsset: linkValue,
    }
    const action = getSaveAction({
      linkValue,
      existingResourceId,
      organizationId,
      body,
      deleteResource,
      updateResource,
      createResource,
    })
    if (!action) return

    try {
      await action.unwrap()
      enqueueSnackbar(t('freeResources.saveSuccess'), { variant: 'success' })
      setIsEditing(false)
    } catch {
      enqueueSnackbar(t('freeResources.saveError'), { variant: 'error' })
    }
  }, [
    resources,
    linkValue,
    organizationId,
    deleteResource,
    updateResource,
    createResource,
    enqueueSnackbar,
    t,
  ])

  if (isSuperAdmin && isEditing) {
    return (
      <SuperAdminEngagementResourcesEditForm
        linkValue={linkValue}
        onLinkChange={setLinkValue}
        onCancel={handleCancel}
        onSave={handleSave}
        isSaving={isSaving}
        isSaveDisabled={isSaveDisabled}
      />
    )
  }

  return (
    <AdminFreeResourcesCard
      resourceAsset={resourceAsset}
      isSuperAdmin={isSuperAdmin}
      isFetching={isFetching}
      onEditClick={handleEditClick}
    />
  )
}
